import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTimes, FaCheck, FaSave, FaPlus } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import Sidebar from './Sidemenu';
import { v4 as uuid } from 'uuid';

const NewSegment = () => {
  const [filterOptions] = useState([
    { label: 'Name', value: 'user_name' },
    { label: 'Email', value: 'user_emailid' },
    { label: 'Age', value: 'user_age' },
    { label: 'Gender', value: 'user_gender' },
    { label: 'Country', value: 'user_country' },
    { label: 'Phonenumber', value: 'user_phonenumber' },
  ]);
  const [valueOptionsPerRow, setValueOptionsPerRow] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const initialName = queryParams.get('name') || '';
  const initialConditions = queryParams.get('conditions') || '[]';

  // Ensure that initialConditions is an array
  const parsedInitialConditions = JSON.parse(initialConditions);
  const initialFilterRows = Array.isArray(parsedInitialConditions) ? parsedInitialConditions : [];

  // Set up default filter rows if none are provided in the URL
  const defaultFilterRows = [
    { id: uuid(), selectedOption: '', filterType: '', filterValue: '' },
    { id: uuid(), selectedOption: '', filterType: '', filterValue: '' },
  ];

  const [selectedRows, setSelectedRows] = useState([]);
  const [filterRows, setFilterRows] = useState(
    initialFilterRows.length > 0 ? initialFilterRows : defaultFilterRows
  );
  
  const [selectedSegment, setSelectedSegment] = useState(null);

  const fetchFieldValues = async (rowIndex) => {
    try {
      const row = filterRows[rowIndex];

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/userDetails/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedOption: row.selectedOption }),
      });

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.filteredUsers) {
          let fieldValues;

          switch (row.selectedOption) {
            case 'user_name':
              fieldValues = responseData.filteredUsers.map((user) => user.user_name);
              break;
            case 'user_emailid':
              fieldValues = responseData.filteredUsers.map((user) => user.user_emailid);
              break;
            case 'user_phonenumber':
              fieldValues = responseData.filteredUsers.map((user) => user.user_phonenumber);
              break;
            case 'user_country':
              fieldValues = [...new Set(responseData.filteredUsers.map((user) => user.user_country))];
              break;
            case 'user_age':
              fieldValues = responseData.filteredUsers.map((user) => user.user_age);
              break;
            case 'user_gender':
              fieldValues = [...new Set(responseData.filteredUsers.map((user) => user.user_gender))];
              break;
            default:
              break;
          }

          if (fieldValues) {
            setValueOptionsPerRow((prev) => {
              const newState = [...prev];
              newState[rowIndex] = [...new Set([...fieldValues])];
              return newState;
            });
          } else {
            console.error('Error fetching field values: fieldValues is undefined');
          }
        } else {
          console.error(
            'Error fetching field values: responseData.filteredUsers is not defined'
          );
        }
      } else {
        console.error('Error fetching field values:', response.statusText);
      }
    } catch (error) {
      console.error('An error occurred while fetching field values:', error);
    }
  };


  useEffect(() => {
    // Fetch field values for each row
    filterRows.forEach((_, index) => fetchFieldValues(index));
  }, [filterRows]);

  useEffect(() => {
    // If editing a segment, set the initial filter conditions
    if (selectedSegment && selectedSegment.filterConditions) {
      setFilterRows(selectedSegment.filterConditions);
    }
  }, [selectedSegment]);

  useEffect(() => {
    // Fetch field values for each row where selectedOption is set
    filterRows.forEach((row, index) => {
      if (row.selectedOption) {
        fetchFieldValues(index);
      }
    });
  }, [filterRows]);


  const handleDropdownChange = async (event, index) => {
    const selectedOptionValue = event.target.value;

    // Use the index to update the specific row's selected option
    const updatedRows = [...filterRows];
    updatedRows[index].selectedOption = selectedOptionValue;
    setFilterRows(updatedRows);

    // Fetch field values only if the selectedOption is valid
    if (isValidSelectedOption(selectedOptionValue)) {
      fetchFieldValues(index);
    }
  };

  const isValidSelectedOption = (selectedOptionValue) => {
    return filterOptions.some((option) => option.value === selectedOptionValue);
  };

  const handleFilterTypeChange = (event, index) => {
    // Use the index to update the specific row's filter type
    const updatedRows = [...filterRows];
    updatedRows[index].filterType = event.target.value;
    setFilterRows(updatedRows);
  };

  const handleFilterValueChange = (event, index) => {
    // Use the index to update the specific row's filter value
    const updatedRows = [...filterRows];
    updatedRows[index].filterValue = event.target.value;
    setFilterRows(updatedRows);
  };

  const handleCheckboxChange = (rowId) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(rowId)) {
        // If the rowId is already in the selectedRows, remove it
        return prevSelectedRows.filter((id) => id !== rowId);
      } else {
        // If the rowId is not in the selectedRows, add it
        return [...prevSelectedRows, rowId];
      }
    });
  };

  const handleApplyFilter = async () => {
    try {
      const selectedFilterRows = filterRows.filter((row) =>
        selectedRows.includes(row.id)
      );

      const filterData = selectedFilterRows.map((row) => ({
        selectedOption: row.selectedOption,
        filterType: row.filterType,
        filterValue: row.filterValue,
      }));


      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/userDetails/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filterData),
      });

      if (response.ok) {
        const responseData = await response.json();
        setFilteredUsers(responseData.filteredUsers);
        setIsFilterApplied(true);
      } else {
        console.error('Error fetching filtered user details:', response.statusText);
      }
    } catch (error) {
      console.error('An error occurred during filter application:', error);
    }
  };

  // State for the details entered in the "Details" tab
  const [segmentDetails, setSegmentDetails] = useState({
    name: initialName,
    aliasName: '',
    description: '',
  });

  const handleCancel = () => {
    console.log('Cancel button clicked');
  };

  const handleApply = () => {
    console.log('Apply button clicked');
  };

  const handleSaveAndClose = async () => {
    navigate('/segment');
    try {
      // Filter out empty filter conditions directly from filterRows
      const nonEmptyFilterRows = filterRows.filter((row) => (
        row.selectedOption.trim() !== '' &&
        row.filterType.trim() !== '' &&
        row.filterValue.trim() !== ''
      ));
  
      // If all conditions are empty, do not proceed with saving
      if (nonEmptyFilterRows.length === 0) {
        console.warn('No non-empty filter conditions to save.');
        return;
      }
  
      // Save filtered user details in the database (SavedDetailsModel)
      const filterConditions = nonEmptyFilterRows.map((row) => ({
        selectedOption: row.selectedOption.trim(),
        filterType: row.filterType.trim(),
        filterValue: row.filterValue.trim(),
      }));
  
      // Create the condition string based on filter conditions
      const conditionString = filterConditions
        .map((condition) => {
          switch (condition.selectedOption) {
            case 'name':
              return `Name=${condition.filterValue}`;
            case 'user_emailid':
              return `Email=${condition.filterValue}`;
            case 'user_phonenumber':
              return `Phone=${condition.filterValue}`;
            case 'user_country':
              return `Country=${condition.filterValue}`;
            case 'user_age':
              return `Age${(condition.filterType)}${condition.filterValue}`;
            case 'user_gender':
              return `Gender=${condition.filterValue}`;
            // Add more cases for other selectedOption values as needed
            default:
              return ''; // Handle unknown options
          }
        })
        .join(' and ');

       
  
      // Log the data that will be sent to the backend
      console.log('Data to be sent to the backend:', {
        name: segmentDetails.name.trim(),
        filterConditions,
        conditionString, // Add the condition string to be stored in the backend
      });
  
      // Check if it's an edit operation
    console.log('Selected Segment:', selectedSegment); // Log selectedSegment
    if (selectedSegment && selectedSegment.id) {
      console.log('Editing existing segment with ID:', selectedSegment.id);

      // If it's an edit, update the existing segment with the same ID
      const updateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveddetails/${selectedSegment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filterConditions,
        }),
      });

      if (updateResponse.ok) {
        console.log('Existing segment updated successfully!');
      } else {
        console.error('Error updating existing segment:', updateResponse.statusText);
        return;
      }
    } else {
      // If it's not an edit, save the new conditions
      const saveResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveddetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: segmentDetails.name.trim(),
          filterConditions,
          conditionString,
        }),
      });

      if (saveResponse.ok) {
        console.log('Filter conditions saved successfully!');
      } else {
        console.error('Error saving filter conditions:', saveResponse.statusText);
        return;
      }
    }
      // Clear the form fields and navigate to the segments page
      setSegmentDetails({ name: '', aliasName: '', description: '' });
      setFilterRows([
        { id: uuid(), selectedOption: '', filterType: '', filterValue: '' },
        { id: uuid(), selectedOption: '', filterType: '', filterValue: '' },
      ]);
      setSelectedSegment(null);
      navigate('/segments');
    } catch (error) {
      console.error('An error occurred during save and close:', error);
    }
  };
  
  
  const handleAddFilterRow = () => {
    setFilterRows((prevRows) => [
      ...prevRows,
      { id: uuid(), selectedOption: '', filterType: '', filterValue: '' },
    ]);
  };

  const handleRemoveFilterRow = (id) => {
    setFilterRows((prevRows) => prevRows.filter((row) => row.id !== id));
  };

  return (
    <div style={{ display: 'flex' }}>
    
    <Sidebar />
    <div className="segment-container">
    <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <h1 style={{ marginLeft: '5%' }}>New Segment</h1>
  <div className="action-buttons" style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '-100px' }}>
    <button className="btn btn-secondary mr-0" onClick={handleCancel}>
      <FaTimes /> Cancel
    </button>
    <button className="btn btn-primary mr-0" onClick={handleApply}>
      <FaCheck /> Apply
    </button>
    <button className="btn btn-success" onClick={handleSaveAndClose}>
      <FaSave /> Save & Close
    </button>
  </div>
</div>

  
      <div className="content mb-5" style={{marginLeft:'2%'}}>
        {/* Segment Details */}
        <div>
          <h3>Segment Details</h3>
          <div className="details-content form-row">
            <div className="form-group col-md-6" style={{marginTop:'35px',marginLeft:'2%'}}>
              <label htmlFor="name ">Name:</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={segmentDetails.name}
                onChange={(e) =>
                  setSegmentDetails({ ...segmentDetails, name: e.target.value })
                }
              />
            </div>
  
            <div className="form-group col-md-6 "style={{marginTop:'35px',marginLeft:'2%'}}>
              <label htmlFor="aliasName">Alias Name:</label>
              <input
                type="text"
                id="aliasName"
                className="form-control"
                value={segmentDetails.aliasName}
                onChange={(e) =>
                  setSegmentDetails({
                    ...segmentDetails,
                    aliasName: e.target.value,
                  })
                }
              />
            </div>
  
            <div className="form-group col-md-12" style={{marginTop:'35px',marginLeft:'2%'}}>
              <label htmlFor="description">Description:</label>
              <textarea
                id="description"
                className="form-control"
                value={segmentDetails.description}
                onChange={(e) =>
                  setSegmentDetails({
                    ...segmentDetails,
                    description: e.target.value,
                  })
                }
              ></textarea>
            </div>
          </div>
        </div>
  
        {/* Segment Filter */}
        <div style={{ width: '100%',marginLeft:'3%', marginBottom:'20%'}}>
          <h3>Segment Filter</h3>
          <div className="filter-content form-row" style={{ width: '100%' }}>
            {filterRows.map((row, index) => (
              <div key={index} className="form-row" style={{ width: '100%' }}>
                <div className="form-group col-md-1" style={{ marginRight: '-5px' }}>
                  <FaTimes
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleRemoveFilterRow(row.id)}
                  />
                </div>
  
                <div className="form-group col-md-1" style={{ marginRight: '-10px' }}>
                  <FaPlus
                    style={{ color: 'green', cursor: 'pointer' }}
                    onClick={handleAddFilterRow}
                  />
                </div>
  
                <div className="form-group col-md-2 " style={{ width: '20px', height: '20px', marginRight: '2%' }}>
                  <div>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={() => handleCheckboxChange(row.id)}
                    />
                  </div>
                </div>
  
                <div className="form-group col-md-2" style={{ marginRight: '20px' }}>
                  <label htmlFor={`logicalOperator-${index}`}>And/Or:</label>
                  <select
                    id={`logicalOperator-${index}`}
                    className="form-control"
                  >
                    <option value="and">And</option>
                    <option value="or">Or</option>
                  </select>
                </div>
  
                <div className="form-group col-md-2" style={{ marginRight: '20px' }}>
                  <label htmlFor={`field-${index}`}>Field:</label>
                  <select
                    id={`field-${index}`}
                    className="form-control"
                    value={row.selectedOption}
                    onChange={(e) => handleDropdownChange(e, index)}
                  >
                    <option value="" disabled>
                      Choose One
                    </option>
                    {filterOptions.map((option, optionIndex) => (
                      <option key={optionIndex} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
  
                <div className="form-group col-md-2" style={{ marginRight: '20px' }}>
                  <label htmlFor={`condition-${index}`}>Condition:</label>
                  <select
                    id={`condition-${index}`}
                    className="form-control"
                    value={row.filterType}
                    onChange={(e) => handleFilterTypeChange(e, index)}
                  >
                    <option value="" disabled>
                      Choose One
                    </option>
                    <option value="equal">Equal</option>
              <option value="notEqual">Not Equal</option>
              <option value="greaterThan">Greater Than</option>
              <option value="greaterThanOrEqual">Greater Than or Equal</option>
              <option value="lessThan">Less Than</option>
              <option value="lessThanOrEqual">Less Than or Equal</option>
              <option value="empty">Empty</option>
              <option value="notEmpty">Not Empty</option>
              <option value="like">Like</option>
              <option value="notLike">Not Like</option>
                  </select>
                </div>
  
                <div className="form-group col-md-2">
                  <label htmlFor={`value-${index}`}>Value:</label>
                  <select
                    id={`value-${index}`}
                    className="form-control"
                    value={row.filterValue}
                    onChange={(e) => handleFilterValueChange(e, index)}
                  >
                    <option value="">Choose One</option>
                    {valueOptionsPerRow[index]?.map((option, optionIndex) => (
                      <option key={optionIndex} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
  
          <div className="form-group col-md-2" >
            <button
              type="button" style={{ width: '60%' }}
              className="btn btn-primary filter-button"
              onClick={handleApplyFilter}
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
  
       {/* Display filtered users */}
       {isFilterApplied && (
        <div className="filtered-users">
          <h2>Filtered Users</h2>
          <ul>
            {filteredUsers.map((user, index) => (
              <li key={index}>
                <strong>User ID:</strong> {user.user_id}
                <br />
                <strong>Name:</strong> {user.name}
                <br />
                <strong>Email:</strong> {user.user_emailid}
                <br />
                <strong>Phone Number:</strong> {user.user_phonenumber}
                <br />
                <strong>Country:</strong> {user.user_country}
                <br />
                <strong>Age:</strong> {user.user_age}
                <br />
                <strong>Gender:</strong> {user.user_gender}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
    </div>
  ); 
};

export default NewSegment;